import { CalendarEvent, EventSourceType, EventType } from './dashboard-stats.model';
import { Request } from './request.model';
import { Booking, BookingStatus } from './booking.model';
import { Announcement, AnnouncementType } from './announcement.model';
import { Invoice, InvoiceStatus } from './invoice.model';
import { timestampToMillis } from '../utils/firestore-timestamp.util';

export class CalendarMapper {
  static mapRequestToEvent(request: Request): CalendarEvent {
    const isUrgent = false; // No priority field in new model
    const createdMs = timestampToMillis(request.createdOn);
    const isOverdue = Boolean(
      request.status === 'pending' &&
        createdMs != null &&
        Date.now() > createdMs + 7 * 24 * 60 * 60 * 1000
    );

    const createdDate =
      createdMs != null ? new Date(createdMs) : new Date();
    const title = request.description || request.providerBusinessName || 'Request';

    return {
      id: parseInt(request.id || '0'),
      title: title,
      date: createdDate,
      type: this.getRequestEventType(request.status),
      description: request.description || '',
      color: this.getRequestColor(request.status, isUrgent, isOverdue),
      sourceType: EventSourceType.REQUEST,
      sourceId: parseInt(request.id || '0'),
      startTime: createdDate,
      endTime: createdDate,
      location: request.type || 'Service Request',
      isAllDay: false,
    };
  }

  static mapBookingToEvent(booking: Booking): CalendarEvent {
    return {
      id: booking.id,
      title: booking.title,
      date: booking.startDate,
      type: EventType.EVENT,
      description: booking.description,
      color: this.getBookingColor(booking.status),
      sourceType: EventSourceType.BOOKING,
      sourceId: booking.id,
      startTime: booking.startTime,
      endTime: booking.endTime,
      location: `Amenity Booking`,
      isAllDay: false,
    };
  }

  static mapAnnouncementToEvent(announcement: Announcement): CalendarEvent {
    const isEmergency = announcement.type === AnnouncementType.EMERGENCY;
    const isEvent = announcement.type === AnnouncementType.EVENT;

    return {
      id: announcement.id,
      title: announcement.title,
      date: announcement.createdDate,
      type: isEvent ? EventType.EVENT : EventType.DEADLINE,
      description: announcement.content,
      color: this.getAnnouncementColor(announcement.type, announcement.priority),
      sourceType: EventSourceType.ANNOUNCEMENT,
      sourceId: announcement.id,
      startTime: announcement.createdDate,
      endTime: announcement.updatedDate,
      isAllDay: !isEmergency,
    };
  }

  static mapInvoiceToEvent(invoice: Invoice): CalendarEvent {
    const isOverdue = invoice.status === InvoiceStatus.OVERDUE;
    const isDueSoon =
      invoice.status === InvoiceStatus.UNPAID &&
      new Date() > new Date(invoice.dueDate.getTime() - 3 * 24 * 60 * 60 * 1000);

    return {
      id: invoice.id,
      title: `Invoice: ${invoice.service}`,
      date: invoice.dueDate,
      type: EventType.DEADLINE,
      description: `Amount: ${invoice.currency} ${invoice.amount}`,
      color: this.getInvoiceColor(invoice.status, isOverdue, isDueSoon),
      sourceType: EventSourceType.INVOICE,
      sourceId: invoice.id,
      startTime: invoice.dueDate,
      endTime: invoice.dueDate,
      isAllDay: true,
    };
  }

  static mapMultipleToEvents(entities: {
    requests?: Request[];
    bookings?: Booking[];
    announcements?: Announcement[];
    invoices?: Invoice[];
  }): CalendarEvent[] {
    const events: CalendarEvent[] = [];

    if (entities.requests) {
      events.push(...entities.requests.map((req) => this.mapRequestToEvent(req)));
    }
    if (entities.bookings) {
      events.push(...entities.bookings.map((booking) => this.mapBookingToEvent(booking)));
    }
    if (entities.announcements) {
      events.push(
        ...entities.announcements.map((announcement) => this.mapAnnouncementToEvent(announcement))
      );
    }
    if (entities.invoices) {
      events.push(...entities.invoices.map((invoice) => this.mapInvoiceToEvent(invoice)));
    }

    return events.sort((a, b) => a.date.getTime() - b.date.getTime());
  }

  private static getRequestEventType(status?: string): EventType {
    switch (status) {
      case 'pending':
        return EventType.DEADLINE;
      case 'in_progress':
        return EventType.MAINTENANCE;
      case 'completed':
        return EventType.EVENT;
      case 'cancelled':
        return EventType.DEADLINE;
      default:
        return EventType.DEADLINE;
    }
  }

  private static getRequestColor(
    status?: string,
    isUrgent: boolean = false,
    isOverdue: boolean = false
  ): string {
    if (isOverdue) return '#dc3545';
    if (isUrgent) return '#fd7e14';
    if (status === 'completed') return '#28a745';
    if (status === 'in_progress') return '#007bff';
    return '#6c757d';
  }

  private static getBookingColor(status: BookingStatus): string {
    switch (status) {
      case BookingStatus.CONFIRMED:
        return '#28a745';
      case BookingStatus.PENDING:
        return '#ffc107';
      case BookingStatus.CANCELLED:
        return '#dc3545';
      case BookingStatus.COMPLETED:
        return '#17a2b8';
      case BookingStatus.NO_SHOW:
        return '#6c757d';
      default:
        return '#6c757d';
    }
  }

  private static getAnnouncementColor(type: AnnouncementType, priority: string): string {
    if (type === AnnouncementType.EMERGENCY) return '#dc3545';
    if (type === AnnouncementType.MAINTENANCE) return '#fd7e14';
    if (priority === 'critical') return '#dc3545';
    if (priority === 'high') return '#fd7e14';
    if (type === AnnouncementType.EVENT) return '#28a745';
    return '#007bff';
  }

  private static getInvoiceColor(
    status: InvoiceStatus,
    isOverdue: boolean,
    isDueSoon: boolean
  ): string {
    if (isOverdue) return '#dc3545';
    if (isDueSoon) return '#fd7e14';
    if (status === InvoiceStatus.PAID) return '#28a745';
    if (status === InvoiceStatus.UNPAID) return '#ffc107';
    return '#6c757d';
  }
}
